import { useEffect, useId, useState } from "react";
import { SciStatGrid } from "@/lib/science/sci-ui";
import { m } from "@/paraglide/messages";

interface Remaining {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  past: boolean;
}

function remainingUntil(target: number, now: number): Remaining {
  let diff = Math.floor((target - now) / 1000);
  const past = diff < 0;
  diff = Math.abs(diff);
  const days = Math.floor(diff / 86400);
  const hours = Math.floor((diff % 86400) / 3600);
  const minutes = Math.floor((diff % 3600) / 60);
  const seconds = diff % 60;
  return { days, hours, minutes, seconds, past };
}

export function CountdownCalc() {
  const id = useId();
  const [target, setTarget] = useState("2027-01-01T00:00");
  // null until mounted → avoids SSR/client hydration mismatch on the clock.
  const [rem, setRem] = useState<Remaining | null>(null);

  useEffect(() => {
    const t = new Date(target).getTime();
    if (Number.isNaN(t)) {
      setRem(null);
      return;
    }
    const tick = () => setRem(remainingUntil(t, Date.now()));
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [target]);

  return (
    <div className="sci-card rounded-2xl space-y-4 p-6 sm:p-8">
      <div className="space-y-1.5">
        <label htmlFor={id} className="block text-sm text-muted-foreground">
          {m.cd_target()}
        </label>
        <input
          id={id}
          type="datetime-local"
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          className="w-full rounded-md border border-black/15 bg-white px-3 py-2 text-sm text-neutral-900"
        />
      </div>

      {rem ? (
        <>
          <div className="sci-result-zone flex items-baseline justify-between gap-4 rounded-xl px-5 py-4">
            <span className="text-sm font-medium text-muted-foreground">
              {rem.past ? m.cd_since() : m.cd_until()}
            </span>
            <span
              data-testid="cd-result"
              className="sci-result font-display text-3xl font-bold sm:text-4xl"
            >
              {rem.days} {m.cd_days()}
            </span>
          </div>
          <SciStatGrid
            items={[
              { label: m.cd_days(), value: String(rem.days) },
              { label: m.cd_hours(), value: String(rem.hours) },
              { label: m.cd_minutes(), value: String(rem.minutes) },
              { label: m.cd_seconds(), value: String(rem.seconds) },
            ]}
          />
        </>
      ) : (
        <p className="text-sm text-muted-foreground">{m.cd_hint()}</p>
      )}
    </div>
  );
}
